import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Phone, Video } from 'lucide-react';
import { toast } from 'react-toastify';

import { useMeetingAction } from '../../../services/calendlyBooking';
import { LegacyTag, isLegacy, kindLabel, meetingWhen } from '../scheduling/MeetingActions';

const firstOf = (name) => String(name || '').trim().split(/\s+/)[0] || '';

// The Schedule's fallback (Nic 2026-10-26): a Sales meeting whose person
// isn't with the viewer anymore (they signed, were dropped, went to Nurture
// or moved to another rep). Those meetings cancel on their own
// (lib/appMeetings), so this only shows if one slips through. Straight to
// the point: it should be canceled, and its host can do it.
const OffBoardMeetingDialog = ({ meeting, viewerUserId, onClose }) => {
  const action = useMeetingAction({
    onSuccess: () => {
      toast.success('Canceled. They get a text and email saying so.');
      onClose();
    },
    onError: (err) => toast.error(err?.response?.data?.msg || "That didn't go through. Try again."),
  });
  useEffect(() => {
    if (!meeting) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [meeting, onClose]);
  if (!meeting) return null;
  const name = `${meeting.leadFirstName || ''} ${meeting.leadLastName || ''}`.trim() || 'This lead';
  const isHost = !!viewerUserId && meeting.hostUserId === viewerUserId;
  const active = meeting.status === 'active';
  const ahead = new Date(meeting.startTime).getTime() > Date.now();
  const canCancel = isHost && active && ahead && meeting.provider === 'app';
  const KindIcon = meeting.kind === 'video' ? Video : Phone;
  const message = !active
    ? 'This meeting is already canceled.'
    : !ahead
      ? 'This meeting already started.'
      : 'This meeting should be canceled.';
  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="off-board-meeting-title"
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.3)] dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10"
      >
        <h3 id="off-board-meeting-title" className="mb-1 text-base font-bold text-slate-800 dark:text-slate-100">
          {name}
        </h3>
        <p className="mb-4 flex items-center gap-1.5 text-[13px] text-slate-500 dark:text-slate-400">
          <KindIcon size={13} className="shrink-0 text-sky-600 dark:text-sky-400" />
          {kindLabel(meeting)} {meetingWhen(meeting.startTime)}
          {meeting.hostName ? ` with ${firstOf(meeting.hostName)}` : ''}
          {isLegacy(meeting) ? <LegacyTag className="ml-1" /> : null}
        </p>
        <p className="mb-0 rounded-xl bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700 dark:!bg-rose-500/10 dark:text-rose-300">
          {message}
        </p>
        {active && ahead && !isHost ? (
          <p className="mb-0 mt-2 text-xs text-slate-400">Only {firstOf(meeting.hostName) || 'its host'} can cancel it.</p>
        ) : null}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={!canCancel}
            onClick={onClose}
            className="flex-1 rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-700 outline-none hover:bg-slate-200 focus:outline-none dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15"
          >
            Close
          </button>
          {canCancel ? (
            <button
              type="button"
              disabled={action.isLoading}
              onClick={() =>
                action.mutate({ mount: 'board', leadId: meeting.leadId, bookingId: meeting.calendlyBookingId, action: 'cancel' })
              }
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-rose-600 py-2.5 text-sm font-semibold text-white outline-none hover:bg-rose-500 focus:outline-none disabled:opacity-60"
            >
              {action.isLoading ? <Loader2 size={14} className="animate-spin" /> : null}
              Cancel meeting
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OffBoardMeetingDialog;
